import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
} from '@mui/material';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import config from '../config';

const server_URL = config.SERVER_URL;

const CustomFields = () => {
  const [open, setOpen] = useState(false);
  const [newCustomFields, setNewCustomFields] = useState({
    method: 'add',
    client_id: '',
    custom_fields: [],
  });
  const [customFieldsList, setCustomFieldsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });
  const [customersWithFields, setCustomersWithFields] = useState([]); // Track which customers already have custom fields

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const handleOpen = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewCustomFields({
      method: 'add',
      client_id: userRole === 2 ? loggedUserId : '',
      custom_fields: [],
    });
    setOpen(true);
  };

  const handleClose = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewCustomFields({
      method: 'add',
      client_id: userRole === 2 ? loggedUserId : '',
      custom_fields: [],
    });
    setOpen(false);
  };

  useEffect(() => {
    fetchCustomFields();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // If role is 2, set selected customer to logged user
    if (userData.role_id === 2) {
      setNewCustomFields(prev => ({ ...prev, client_id: userData.id }));
    }
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
    }
  }, [userRole, loggedUserId]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: loggedUserId,
          role_id: userRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        // For filter dropdown: role 1 sees all, role 2 sees only logged customer
        if (userRole === 1) {
          setFilterCustomers(data.data);
        } else if (userRole === 2 && loggedUserId) {
          setFilterCustomers(data.data.filter(customer => customer.id === loggedUserId));
        }
        
        setCustomers(data.data);
        
        // Auto-select if only one customer
        if (data.data.length === 1) {
          setNewCustomFields(prev => ({ ...prev, client_id: data.data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomFields = async (filterCustomerId = null) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id,
        user_role: userData.role_id,
      };
      
      // Add customer filter if provided
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.client_id = customerIdValue;
        console.log('Filtering by client_id:', customerIdValue);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/custom_fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch custom fields');
      }

      const data = await response.json();
      console.log('Received custom fields data:', data);
      
      const customFieldsData = Array.isArray(data) ? data : data.data || [];
      setCustomFieldsList(customFieldsData);
      
      // Track which customers already have custom fields
      const customerIdsWithFields = customFieldsData.map(cf => cf.client_id);
      setCustomersWithFields(customerIdsWithFields);
      
      if (customFieldsData.length === 0) {
        console.log('No custom fields found matching the filter criteria');
      } else {
        console.log(`Found ${customFieldsData.length} custom field record(s)`);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setCustomFieldsList([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFilter = () => {
    fetchCustomFields(customerFilter);
  };
  
  const handleClearFilter = () => {
    setCustomerFilter('');
    fetchCustomFields();
  };

  const handleAddCustomField = () => {
    if (newCustomFields.custom_fields.length >= 6) {
      showToast('Maximum 6 custom field sets allowed', 'warning');
      return;
    }
    
    const newField = {
      tag: `cust${newCustomFields.custom_fields.length + 1}`,
      name: '',
      key: '',
    };
    
    setNewCustomFields(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, newField],
    }));
  };

  const handleRemoveCustomField = (index) => {
    setNewCustomFields(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index).map((field, idx) => ({
        ...field,
        tag: `cust${idx + 1}`,
      })),
    }));
  };

  const generateKeyFromName = (name) => {
    if (!name) return '';
    
    // Convert to lowercase, replace spaces with underscores, keep only lowercase letters, numbers, and underscores
    let key = name
      .toLowerCase()
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^a-z0-9_]/g, '')  // Remove everything except lowercase letters, numbers, and underscores
      .replace(/_+/g, '_')  // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, '');  // Remove leading/trailing underscores
    
    // Add [ prefix and ] suffix
    return key ? `[${key}]` : '';
  };

  const handleCustomFieldChange = (index, field, value) => {
    setNewCustomFields(prev => {
      const updatedCustomFields = prev.custom_fields.map((cf, i) => {
        if (i === index) {
          const updatedField = { ...cf, [field]: value };
          // Auto-generate key when name changes
          if (field === 'name') {
            updatedField.key = generateKeyFromName(value);
          }
          return updatedField;
        }
        return cf;
      });
      
      return {
        ...prev,
        custom_fields: updatedCustomFields,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate customer selection
    const clientId = newCustomFields.client_id;
    if (clientId === '' || clientId === null || clientId === undefined) {
      showToast('Please select a customer', 'error');
      return;
    }
    
    // Check if customer already has custom fields (only when adding, not editing)
    if (newCustomFields.method === 'add' && customersWithFields.includes(clientId)) {
      showToast('This customer already has custom fields. Only one custom field record is allowed per customer.', 'error');
      return;
    }
    
    // Validate at least one custom field set
    if (newCustomFields.custom_fields.length === 0) {
      showToast('Please add at least one custom field set', 'error');
      return;
    }
    
    // Validate all custom fields have name and key
    for (let i = 0; i < newCustomFields.custom_fields.length; i++) {
      const cf = newCustomFields.custom_fields[i];
      if (!cf.name || !cf.key) {
        showToast(`Please fill in name and key for custom field set ${i + 1}`, 'error');
        return;
      }
    }
    
    setIsSubmitting(true);
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const customFieldsData = {
      method: newCustomFields.method,
      client_id: clientId,
      custom_fields: newCustomFields.custom_fields,
      user_id: userData.id,
      is_subuser: userData.is_subuser || false,
      subuser_id: userData.subuser_id || null,
      logged_email: userData.email || '',
    };

    if (newCustomFields.method === 'update') {
      customFieldsData.id = newCustomFields.id;
    }

    try {
      const response = await fetch(`${server_URL}/api/custom_fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customFieldsData),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchCustomFields(customerFilter);
        handleClose();
        showToast(data.message || 'Custom fields saved successfully', 'success');
      } else {
        showToast(data.error || 'Failed to save custom fields', 'error');
      }
    } catch (error) {
      console.error(`Error ${newCustomFields.method}ing custom fields:`, error);
      showToast('An error occurred while saving custom fields', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (id, client_id, custom_fields) => {
    setNewCustomFields({
      method: 'update',
      id,
      client_id: client_id !== undefined && client_id !== null ? Number(client_id) : '',
      custom_fields: custom_fields || [],
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom fields record?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/custom_fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
          logged_email: userData.email || '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete custom fields');
      }

      const data = await response.json();
      if (data.status === '1') {
        // Find the deleted record to get its client_id
        const deletedRecord = customFieldsList.find(cf => cf.id === id);
        const deletedClientId = deletedRecord ? deletedRecord.client_id : null;
        
        await fetchCustomFields(customerFilter);
        showToast('Custom fields deleted successfully', 'success');
        
        // Update customersWithFields after deletion - remove the client_id if it no longer has custom fields
        // This will be updated by fetchCustomFields, but we can also update it here for immediate feedback
        if (deletedClientId) {
          setCustomersWithFields(prev => prev.filter(clientId => clientId !== deletedClientId));
        }
      } else {
        showToast(data.error || 'Failed to delete custom fields', 'error');
      }
    } catch (error) {
      console.error('Error deleting custom fields:', error);
      showToast('Failed to delete custom fields', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1>Custom Fields</h1>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add Custom Fields
        </Button>
      </div>
      
      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {filterCustomers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchCustomFields(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}
      
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading custom fields...</p>
          </div>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {userRole === 1 && <TableCell>Client ID</TableCell>}
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Custom Fields</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customFieldsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 4 : 2} align="center" sx={{ py: 5 }}>
                    <Box>
                      <h5 style={{ color: '#666' }}>No custom fields found</h5>
                      {customerFilter !== '' && (
                        <p style={{ color: '#999', fontSize: '0.875rem', marginTop: '8px' }}>
                          Try clearing the filter or selecting a different customer
                        </p>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                customFieldsList.map((row) => (
                  <TableRow key={row.id}>
                    {userRole === 1 && <TableCell>{row.client_id}</TableCell>}
                    {userRole === 1 && (
                      <TableCell>
                        {row.client_username || 'N/A'}
                        {row.client_email && (
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {row.client_email}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {row.custom_fields && row.custom_fields.length > 0 ? (
                        <Box>
                          {row.custom_fields.map((cf, idx) => (
                            <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="body2">
                                <strong>Tag:</strong> {cf.tag} | <strong>Name:</strong> {cf.name} | <strong>Key:</strong> {cf.key}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">No custom fields</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleEdit(
                          row.id,
                          row.client_id,
                          row.custom_fields
                        )}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(row.id)}
                        disabled={deletingId === row.id}
                      >
                        {deletingId === row.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="add-custom-fields-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <h2>{newCustomFields.method === 'add' ? 'Add Custom Fields' : 'Edit Custom Fields'}</h2>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={newCustomFields.client_id === 0 ? 0 : (newCustomFields.client_id || '')}
                  onChange={(e) => {
                    const selectedCustomerId = e.target.value === '' ? '' : Number(e.target.value);
                    // Check if customer already has custom fields (only when adding, not editing)
                    if (newCustomFields.method === 'add' && customersWithFields.includes(selectedCustomerId)) {
                      showToast('This customer already has custom fields. Only one custom field record is allowed per customer.', 'warning');
                      return;
                    }
                    setNewCustomFields(prev => ({ ...prev, client_id: selectedCustomerId }));
                  }}
                  label="Customer"
                  disabled={userRole === 2} // Disable for role 2 users (only their own account)
                >
                  {customers.map((customer) => {
                    const hasFields = customersWithFields.includes(customer.id);
                    const isEditing = newCustomFields.method === 'update' && newCustomFields.client_id === customer.id;
                    const isDisabled = hasFields && newCustomFields.method === 'add' && !isEditing;
                    
                    return (
                      <MenuItem 
                        key={customer.id} 
                        value={customer.id}
                        disabled={isDisabled}
                      >
                        {customer.username} ({customer.email})
                        {isDisabled && ' - Already has custom fields'}
                      </MenuItem>
                    );
                  })}
                </Select>
                {newCustomFields.method === 'add' && customersWithFields.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Note: Customers with existing custom fields are disabled. Only one custom field record is allowed per customer.
                  </Typography>
                )}
              </FormControl>

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Custom Field Sets (Max 6)</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddCustomField}
                    disabled={newCustomFields.custom_fields.length >= 6}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <FaPlus /> Add Set
                  </Button>
                </Box>

                {newCustomFields.custom_fields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No custom field sets added. Click "Add Set" to add one.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {[...newCustomFields.custom_fields].reverse().map((cf, index) => {
                      // Calculate original index for proper handling
                      const originalIndex = newCustomFields.custom_fields.length - 1 - index;
                      return (
                      <Box key={originalIndex} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, bgcolor: '#f9f9f9' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Set {originalIndex + 1} - Tag: {cf.tag}</Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveCustomField(originalIndex)}
                          >
                            <FaTrash />
                          </IconButton>
                        </Box>
                        <TextField
                          fullWidth
                          label="Name"
                          value={cf.name}
                          onChange={(e) => handleCustomFieldChange(originalIndex, 'name', e.target.value)}
                          required
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          label="Key"
                          value={cf.key}
                          required
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {newCustomFields.method === 'add' ? 'Adding...' : 'Updating...'}
                    </>
                  ) : (
                    newCustomFields.method === 'add' ? 'Add Custom Fields' : 'Update Custom Fields'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastState(prev => ({ ...prev, open: false }))}
          onClick={() => setToastState(prev => ({ ...prev, open: false }))}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CustomFields;

